import React, { FC, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  ListItemButton,
  ListItemText,
  List,
  Popover,
  Button,
  Avatar,
  Link,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ChevronDown,
  AtSign,
  ArrowUpCircle,
  ExternalLink as OpenInNewIcon,
  X as CloseIcon,
} from 'lucide-mui';
import Dashboard from '../dashboard';
import type { RootState } from '../../reducers';
import { setSearchQuery, clearSearch, clearSearchAnswer } from './searchSlice';
import { getSelectedSource, searchRecordings } from './searchAPI';
import { formatTimestamp } from '../library/files/MultiAskAI/Results';
import Thinking from '../selectedVideo/chat/history/Thinking';

const FilterDropdown = ({
  anchorEl,
  onClose,
  selectedFolders,
  onFoldersChange,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  selectedFolders: string[];
  onFoldersChange: (folders: string[]) => void;
}) => {
  const { folders } = useSelector((state: RootState) => ({
    folders: state.library.folders,
  }));
  const [folderSearch, setFolderSearch] = useState('');

  const filteredFolders = folders.data.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearch.toLowerCase())
  );

  const handleFolderToggle = (folderId: string) => {
    if (selectedFolders.includes(folderId)) {
      onFoldersChange(selectedFolders.filter((id) => id !== folderId));
      return;
    }
    onFoldersChange([...selectedFolders, folderId]);
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        mt: 1,
      }}
    >
      <Box sx={{ width: 250, p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Filter by folders
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search folders..."
          value={folderSearch}
          onChange={(e) => setFolderSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon width={16} height={16} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.default',
            },
          }}
        />
        <List sx={{ py: 0.5, maxHeight: 300, overflow: 'auto' }}>
          {filteredFolders.length > 0 ? (
            filteredFolders.map((folder) => (
              <ListItemButton
                key={folder._id}
                onClick={() => handleFolderToggle(folder._id)}
                sx={{
                  py: 1,
                  px: 2,
                  '&:hover': {
                    backgroundColor: 'secondary.light',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    border: 1,
                    borderColor: selectedFolders.includes(folder._id)
                      ? 'primary.main'
                      : 'divider',
                    borderRadius: 0.5,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: selectedFolders.includes(folder._id)
                      ? 'primary.main'
                      : 'transparent',
                  }}
                >
                  {selectedFolders.includes(folder._id) && (
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        bgcolor: 'common.white',
                        clipPath:
                          'polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)',
                      }}
                    />
                  )}
                </Box>
                <ListItemText
                  primary={folder.name}
                  primaryTypographyProps={{
                    variant: 'body2',
                  }}
                />
              </ListItemButton>
            ))
          ) : (
            <ListItemText
              sx={{ px: 2 }}
              primary="No folders found"
              primaryTypographyProps={{
                color: 'text.secondary',
                variant: 'body2',
              }}
            />
          )}
        </List>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            pt: 1,
            mt: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button
            size="small"
            onClick={() => onFoldersChange([])}
            sx={{ mr: 1 }}
          >
            Clear
          </Button>
          <Button variant="contained" size="small" onClick={onClose}>
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

const FileList = ({
  anchorEl,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) => {
  const dispatch = useDispatch();
  const [fileSearch, setFileSearch] = useState('');
  // Fake list of recent files
  const recentFiles = [
    {
      _id: '1',
      name: 'Weekly Team Meeting.mp4',
      createdAt: '2024-01-15T10:00:00Z',
      thumbUrl: '',
    },
    {
      _id: '2',
      name: 'Product Demo v2.mp4',
      createdAt: '2024-01-14T15:30:00Z',
      thumbUrl: '',
    },
    {
      _id: '3',
      name: 'Bug Report #123.mp4',
      createdAt: '2024-01-14T09:15:00Z',
      thumbUrl: '',
    },
    {
      _id: '4',
      name: 'Design Review.mp4',
      createdAt: '2024-01-13T16:45:00Z',
      thumbUrl: '',
    },
    {
      _id: '5',
      name: 'Onboarding Tutorial.mp4',
      createdAt: '2024-01-13T11:20:00Z',
      thumbUrl: '',
    },
  ];

  const filteredFiles = recentFiles.filter((file) =>
    file.name.toLowerCase().includes(fileSearch.toLowerCase())
  );

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            width: '100%',
            maxWidth: 600,
            mt: 1,
          },
        },
      }}
    >
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search files..."
          value={fileSearch}
          onChange={(e) => setFileSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon width={16} height={16} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              backgroundColor: 'background.default',
            },
          }}
        />
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ mt: 2, mb: 0.5 }}
        >
          Recent Files
        </Typography>
      </Box>
      <List sx={{ maxHeight: 300, overflow: 'auto', pb: 0 }}>
        {filteredFiles.length > 0 ? (
          filteredFiles.map((file) => (
            <ListItemButton
              key={file._id}
              onClick={() => {
                const input = document.querySelector(
                  'input'
                ) as HTMLInputElement;
                if (input) {
                  // Find the position of the last @ symbol
                  const lastAtPos = input.value.lastIndexOf('@');
                  if (lastAtPos !== -1) {
                    // Keep everything before the @, add the file name, and keep everything after the current cursor
                    const curPos = input.selectionStart || 0;
                    const beforeAt = input.value.slice(0, lastAtPos);
                    const afterCursor = input.value.slice(curPos);
                    const newValue = `${beforeAt}@${file.name} ${afterCursor}`;

                    // Update Redux state instead of input value directly
                    dispatch(setSearchQuery(newValue));

                    // Move cursor after the inserted file name and space
                    setTimeout(() => {
                      if (input) {
                        const newCursorPos = lastAtPos + file.name.length + 2; // +2 for the @ symbol and space
                        input.setSelectionRange(newCursorPos, newCursorPos);
                        input.focus();
                      }
                    }, 0);
                  }
                }
                onClose();
              }}
              sx={{
                py: 1,
                px: 2,
                '&:hover': {
                  backgroundColor: 'secondary.light',
                },
              }}
            >
              <Avatar
                src={file.thumbUrl}
                variant="rounded"
                sx={{ width: 40, height: 40, mr: 2 }}
              >
                {file.name[0]}
              </Avatar>
              <ListItemText
                primary={file.name}
                secondary={formatDate(file.createdAt)}
                primaryTypographyProps={{
                  noWrap: true,
                }}
              />
            </ListItemButton>
          ))
        ) : (
          <ListItemText
            primary="No files found"
            primaryTypographyProps={{
              color: 'text.secondary',
            }}
          />
        )}
      </List>
    </Popover>
  );
};

const SearchHeader = () => {
  const dispatch = useDispatch();
  const { folders } = useSelector((state: RootState) => ({
    folders: state.library.folders,
  }));
  const searchQuery = useSelector((state: RootState) => state.search.query);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [fileAnchor, setFileAnchor] = useState<HTMLElement | null>(null);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const searchContainerRef = React.useRef<HTMLDivElement>(null);

  const getButtonText = () => {
    if (selectedFolders.length === 0) return 'All';
    if (selectedFolders.length === 1) {
      const folder = folders.data.find((f) => f._id === selectedFolders[0]);
      return folder?.name || 'All';
    }
    return `${selectedFolders.length} Folders`;
  };

  const beginSearch = () => {
    dispatch(clearSearchAnswer());
    dispatch(searchRecordings(searchQuery.trim()));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    const lastChar = query.charAt(query.length - 1);

    dispatch(setSearchQuery(query));

    // Show file popover if @ was just typed
    if (lastChar === '@') {
      setFileAnchor(searchContainerRef.current);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Close popover on escape
    if (e.key === 'Escape' && fileAnchor) {
      setFileAnchor(null);
    }
    // Trigger search on enter
    if (e.key === 'Enter' && searchQuery.trim()) {
      beginSearch();
    }
  };

  const handleAtClick = () => {
    const input = searchInputRef.current;
    if (!input) return;

    const curPos = input.selectionStart || 0;
    const newQuery = `${searchQuery.slice(0, curPos)}@${searchQuery.slice(
      curPos
    )}`;
    dispatch(setSearchQuery(newQuery));

    // Use the search container as the anchor element
    setFileAnchor(searchContainerRef.current);

    // Focus and move cursor after @
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(curPos + 1, curPos + 1);
    }, 0);
  };

  const handleSubmit = () => {
    if (searchQuery.trim()) {
      beginSearch();
    }
  };

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Box
        ref={searchContainerRef}
        sx={{ maxWidth: 600, mx: 'auto', width: '100%' }}
      >
        <TextField
          fullWidth
          autoFocus
          size="small"
          inputRef={searchInputRef}
          placeholder="Ask anything or select..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}
              >
                <Box
                  component="button"
                  onClick={(e) => setFilterAnchor(e.currentTarget)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    border: 0,
                    bgcolor: 'transparent',
                    color: 'text.secondary',
                    p: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    opacity: 0.5,
                    transition:
                      'opacity 0.2s, background-color 0.2s, color 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                      opacity: 1,
                    },
                  }}
                >
                  {getButtonText()}
                  <ChevronDown width={14} height={14} />
                </Box>
                <Box
                  component="button"
                  onClick={handleAtClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 0,
                    bgcolor: 'transparent',
                    color: 'text.secondary',
                    p: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    opacity: 0.3,
                    transition:
                      'opacity 0.2s, background-color 0.2s, color 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'text.primary',
                      opacity: 1,
                    },
                  }}
                >
                  <AtSign width={16} height={16} />
                </Box>
                <Box
                  component="button"
                  onClick={handleSubmit}
                  disabled={!searchQuery.trim()}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 0,
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    p: 0.5,
                    borderRadius: 1,
                    cursor: searchQuery.trim() ? 'pointer' : 'default',
                    opacity: searchQuery.trim() ? 1 : 0.3,
                    transition:
                      'opacity 0.2s, background-color 0.2s, color 0.2s',
                    '&:not(:disabled):hover': {
                      bgcolor: 'action.hover',
                      color: 'primary.dark',
                    },
                  }}
                >
                  <ArrowUpCircle width={16} height={16} />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper',
              py: 0.5,
            },
          }}
        />
        <FilterDropdown
          anchorEl={filterAnchor}
          onClose={() => setFilterAnchor(null)}
          selectedFolders={selectedFolders}
          onFoldersChange={setSelectedFolders}
        />
        <FileList anchorEl={fileAnchor} onClose={() => setFileAnchor(null)} />
      </Box>
    </Box>
  );
};

interface VideoPreviewProps {
  fileId: string;
  timestamp: number;
  onClose(): void;
}
const VideoPreview: FC<VideoPreviewProps> = (props) => {
  const { fileId, timestamp, onClose } = props;
  const { sources } = useSelector((state: RootState) => state.search);
  const selectedSource = sources[fileId] ? sources[fileId] : null;

  if (!selectedSource) return null;

  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'secondary.light',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          maxWidth: '800px',
          mx: 'auto',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            pl: 1,
            pr: 0.5,
            height: 48,
            borderBottom: 1,
            borderColor: 'secondary.light',
            bgcolor: 'background.paper',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Link
            href={`${
              window.location.pathname.includes('/app') ? '/app/#' : '/#'
            }/library/${selectedSource.teamId}/recents/${selectedSource._id}`}
            underline="hover"
            sx={{
              color: 'text.primary',
              '&:hover': {
                color: 'primary.main',
              },
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography variant="body2" color="inherit">
              Source: {selectedSource.name} at {formatTimestamp(`${timestamp}`)}
            </Typography>
            <OpenInNewIcon sx={{ fontSize: 14, opacity: 0.7 }} />
          </Link>
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              onClose();
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ width: '100%', lineHeight: 0 }}>
          {selectedSource.streams?.includes('video') ? (
            <Box
              sx={{
                position: 'relative',
                height: '30vh',
                maxHeight: '30vh',
                overflow: 'hidden',
                bgcolor: 'black',
              }}
            >
              <video
                controls
                autoPlay
                src={selectedSource.url}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'contain',
                }}
                onLoadedMetadata={(e) => {
                  const video = e.target as HTMLVideoElement;
                  video.currentTime = timestamp;
                }}
              >
                <source src={selectedSource.url} type="video/mp4" />
                <track kind="captions" />
                Your browser does not support the video tag.
              </video>
            </Box>
          ) : (
            <audio
              controls
              autoPlay
              src={selectedSource.url}
              style={{
                width: '100%',
                display: 'block',
              }}
              onLoadedMetadata={(e) => {
                const audio = e.target as HTMLAudioElement;
                audio.currentTime = timestamp;
              }}
            >
              <source src={selectedSource.url} type="audio/mp3" />
              <track kind="captions" />
              Your browser does not support the audio tag.
            </audio>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const SearchResults = () => {
  const { query, answer, status, sources } = useSelector(
    (state: RootState) => state.search
  );
  const dispatch = useDispatch();

  const [showResults, setShowResults] = useState(true);
  const [activeSource, setActiveSource] = useState<{
    fileId?: string;
    timestamp?: string;
  } | null>(null);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        setShowResults(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!query || !showResults) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {!query
            ? 'Start typing to search through your recordings'
            : 'Press Enter to search'}
        </Typography>
      </Box>
    );
  }

  const handleSourceClick = (fileId: string, timestamp: string) => {
    if (!fileId || !timestamp) return;

    if (!sources[fileId]) {
      dispatch(getSelectedSource(fileId));
    }

    setActiveSource((prev) => {
      if (prev?.fileId === fileId && prev.timestamp === timestamp) {
        const mediaElement = document.querySelector(
          'video, audio'
        ) as HTMLMediaElement;
        if (mediaElement) {
          mediaElement.pause();
          mediaElement.currentTime = parseInt(timestamp, 10);
        }
        return prev;
      }

      setShowVideoPlayer(true);
      return { fileId, timestamp };
    });
  };

  const handleSourceClose = () => {
    setShowVideoPlayer(false);
    setActiveSource(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
        {status === 'loading' && (
          <Collapse in timeout="auto" orientation="vertical">
            <Thinking />
          </Collapse>
        )}
        <Box
          sx={{
            wordBreak: 'break-word',
            width: '100%',
            '& img': { maxWidth: '100%' },
            '& pre': {
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              width: '100%',
            },
            '& code': {
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
            },
            '& p': {
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              width: '100%',
            },
            '& ul, & ol': {
              width: '100%',
              paddingLeft: '20px',
            },
            '& li': {
              width: '100%',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ href, children }) => {
                if (href?.includes('@')) {
                  const [fileId, timestamp] = href.split('@');
                  const isActive =
                    activeSource?.fileId === fileId &&
                    activeSource?.timestamp === timestamp;

                  return (
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.preventDefault();
                        handleSourceClick(fileId, timestamp);
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '1.25rem',
                        height: '1.25rem',
                        borderRadius: '50%',
                        backgroundColor: isActive
                          ? 'secondary.dark'
                          : 'secondary.light',
                        border: 1,
                        borderColor: 'secondary.main',
                        color: 'secondary.contrastText',
                        cursor: 'pointer',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                        ml: 0.5,
                        mr: 0.5,
                        verticalAlign: 'baseline',
                        position: 'relative',
                        top: '-0.125em',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        minWidth: '1.25rem',
                      }}
                    >
                      →
                    </Box>
                  );
                }
                return <Link href={href}>{children}</Link>;
              },
            }}
          >
            {answer}
          </ReactMarkdown>
        </Box>

        {activeSource?.fileId && activeSource?.timestamp && showVideoPlayer && (
          <VideoPreview
            onClose={handleSourceClose}
            fileId={activeSource.fileId}
            timestamp={Number(activeSource.timestamp)}
          />
        )}
      </Box>
    </Box>
  );
};

export const SearchPage = () => {
  const dispatch = useDispatch();

  // Update page title and cleanup
  useEffect(() => {
    document.title = 'Search - ScreenApp';
    return () => {
      document.title = 'ScreenApp';
      dispatch(clearSearch());
    };
  }, [dispatch]);

  return (
    <Dashboard>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <SearchHeader />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <SearchResults />
        </Box>
      </Box>
    </Dashboard>
  );
};

export default SearchPage;
